import {
  CalendarDotsIcon,
  CheckCircleIcon,
  CopyIcon,
  MapPinIcon,
  type IconProps,
} from "@phosphor-icons/react";
import { useInViewport } from "ahooks";
import dayjs from "dayjs";
import { useRef, useState, type ElementType, type ReactNode } from "react";
import { useIntl } from "react-intl";

import { ButtonBase } from "@/components/ButtonBase/ButtonBase";
import { IconButton } from "@/components/IconButton/IconButton";
import { IconCheck, IconTravellers } from "@/components/Icons";
import { CaretRightIcon } from "@/components/Icons/rtlReadyIcons";
import { LazyImage } from "@/components/Image/LazyImage";
import { BookedHotelDocumentsSection } from "@/features/hotels/components/hotel/BookedHotelDocumentsSection";
import { BookedHotelTravellersDrawer } from "@/features/hotels/components/hotel/BookedHotelTravellersDrawer";
import type { HotelData } from "@/features/hotels/types/types";
import { formatHotelName } from "@/features/hotels/utils/names";
import type { Traveller } from "@/features/travellers/types/types";
import type {
  FindOrderByIdAndUserIdOutputData,
  FindUserReservationsExternalHotelReservation,
} from "@/types/codegen";
import { copyToClipboardWithFeedback } from "@/utils/copyToClipboard";

type DetailsRowProps = {
  icon: ElementType<IconProps>;
  label: ReactNode;
  value: ReactNode;
};

const DetailsRow = ({ icon: Icon, label, value }: DetailsRowProps) => {
  return (
    <div className="flex w-full gap-2">
      <Icon className="size-5 shrink-0" />
      <div className="w-full space-y-1">
        <div className="text-base font-medium text-text-primary">{label}</div>
        <div className="text-sm text-text-tertiary">{value}</div>
      </div>
    </div>
  );
};

type Props = {
  reservation: FindUserReservationsExternalHotelReservation;
  hotel: HotelData;
  travellers: Traveller[];
  leadTravellerId: string | null;
  orderData: FindOrderByIdAndUserIdOutputData;
};

export const BookedHotelCard = ({
  hotel,
  reservation,
  travellers,
  leadTravellerId,
  orderData,
}: Props) => {
  const intl = useIntl();
  const [travellersDrawerVisible, setTravellersDrawerVisible] = useState(false);
  const documentsSectionRef = useRef<HTMLDivElement | null>(null);
  const [, documentsSectionVisibleRatio] = useInViewport(documentsSectionRef, {
    threshold: [0, 0.1, 0.25],
  });

  const startDate = dayjs(reservation.serviceDates.startDate.split("T")[0]);
  const endDate = dayjs(reservation.serviceDates.endDate.split("T")[0]);
  const numberOfDays = endDate.diff(startDate, "days") + 1;

  return (
    <>
      <div className="section-card">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="me-4 space-y-0.5">
              <div className="text-xl font-semibold text-text-primary">
                {intl.formatMessage({
                  defaultMessage: "Booking is confirmed!",
                  description:
                    "Title of the booked card on booking success screen",
                })}
              </div>
              <div className="text-sm text-text-tertiary">
                {formatHotelName(reservation.hotel.name)}
              </div>
            </div>
            <div className="relative">
              <div className="flex h-22.5 w-20 shrink-0 items-center overflow-clip rounded-lg bg-gray-100 text-center text-xs text-text-tertiary">
                {reservation.hotel.mainImageUrl ? (
                  <LazyImage
                    className="h-full w-full object-cover"
                    sentryElementClassName="self-start"
                    src={reservation.hotel.mainImageUrl}
                    alt={reservation.hotel.name}
                  />
                ) : (
                  <span className="grow">
                    {intl.formatMessage({
                      defaultMessage: "No image available",
                      description:
                        "Fallback alt text shown when no hotel or room image is available",
                    })}
                  </span>
                )}
              </div>
              <IconCheck className="absolute -start-4 -bottom-1 size-8 rounded-full bg-white" />
            </div>
          </div>

          <div className="mb-6">
            <div className="-my-3 divide-y divide-stroke-neutral-soft *:w-full *:py-3">
              <DetailsRow
                icon={CalendarDotsIcon}
                label={intl.formatMessage(
                  {
                    defaultMessage:
                      "{checkInDate, date, ::EEEMMMd} - {checkOutDate, date, ::EEEMMMd}",
                    description: "Formatted check in and checkout date",
                  },
                  {
                    checkInDate: startDate.toDate(),
                    checkOutDate: endDate.toDate(),
                  },
                )}
                value={intl.formatMessage(
                  {
                    defaultMessage:
                      "{daysCount, plural, one {# Day} other {# Days}} - {nightsCount, plural, one {# Night} other {# Nights}}",
                    description: "Number of days and nights of hotel booking",
                  },
                  {
                    daysCount: numberOfDays,
                    nightsCount: numberOfDays - 1,
                  },
                )}
              />

              <DetailsRow
                icon={MapPinIcon}
                label={intl.formatMessage({
                  defaultMessage: "Location",
                  description: "Label for the location of the hotel",
                })}
                value={
                  <div className="flex w-full items-center gap-2">
                    <p className="me-auto">{hotel.address}</p>
                    <IconButton
                      size="small"
                      disablePadding
                      onClick={() =>
                        copyToClipboardWithFeedback(
                          hotel.address,
                          intl.formatMessage({
                            defaultMessage: "Hotel address copied",
                            description:
                              "Snackbar shown after copying the hotel address",
                          }),
                        )
                      }
                      aria-label={intl.formatMessage({
                        defaultMessage: "Copy address",
                        description:
                          "Button to copy the hotel address to clipboard",
                      })}
                    >
                      <CopyIcon color="var(--color-text-tertiary)" size={20} />
                    </IconButton>
                  </div>
                }
              />

              <ButtonBase
                className="flex items-center justify-between text-start"
                onClick={() => {
                  setTravellersDrawerVisible(true);
                }}
              >
                <DetailsRow
                  icon={IconTravellers}
                  label={intl.formatMessage({
                    defaultMessage: "Travellers",
                    description: "Label of hotel booking travellers",
                  })}
                  value={intl.formatMessage(
                    {
                      defaultMessage:
                        "{travellersCount, plural, one {# Guest} other {# Guests}}",
                      description: "Number of guests in hotel booking ",
                    },
                    {
                      travellersCount: reservation.travelers.length,
                    },
                  )}
                />

                <CaretRightIcon className="size-5 shrink-0" />
              </ButtonBase>

              <DetailsRow
                icon={CheckCircleIcon}
                label={intl.formatMessage({
                  defaultMessage: "Confirmation number",
                  description: "Label for hotel booking confirmation number",
                })}
                value={reservation.reference.external}
              />
            </div>
          </div>

          <div ref={documentsSectionRef}>
            <BookedHotelDocumentsSection
              orderData={orderData}
              inDrawer={(documentsSectionVisibleRatio ?? 0) < 0.1}
            />
          </div>
        </div>
      </div>
      <BookedHotelTravellersDrawer
        open={travellersDrawerVisible}
        travellers={travellers}
        leadTravellerId={leadTravellerId}
        onClose={() => setTravellersDrawerVisible(false)}
      />
    </>
  );
};
